<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';

// Two-level cascade: pick a kanban, then a stage within it. Only the stage id is emitted — the
// kanban is derived from the stage on the backend (kanban_stage.kanban_id), so we never need to
// persist the kanban id separately, just use it here to narrow the stage dropdown.
const props = defineProps({
  modelValue: {
    type: [Number, String],
    default: null,
  },
  excludeStageId: {
    type: [Number, String],
    default: null,
  },
  dropdownMaxHeight: {
    type: String,
    default: 'max-h-80',
  },
});

const emit = defineEmits(['update:modelValue']);

const store = useStore();
const getters = useStoreGetters();

const kanbans = computed(() => getters['kanbans/getKanbans'].value || []);

const findKanbanIdForStage = stageId => {
  if (!stageId) return null;
  const owner = kanbans.value.find(kanban =>
    (kanban.kanban_stages || []).some(stage => stage.id === Number(stageId))
  );
  return owner ? owner.id : null;
};

const selectedKanbanId = ref(findKanbanIdForStage(props.modelValue));
const selectedStageId = ref(props.modelValue || null);

watch(
  () => props.modelValue,
  value => {
    selectedStageId.value = value || null;
    selectedKanbanId.value = findKanbanIdForStage(value);
  }
);

const kanbanOptions = computed(() =>
  kanbans.value.map(kanban => ({ id: kanban.id, name: kanban.name }))
);

const stageOptions = computed(() => {
  if (!selectedKanbanId.value) return [];
  return getters['kanbans/getStagesByKanbanId']
    .value(selectedKanbanId.value)
    .filter(stage => stage.id !== Number(props.excludeStageId))
    .map(stage => ({ id: stage.id, name: stage.name }));
});

const selectedKanbanOption = computed(
  () =>
    kanbanOptions.value.find(option => option.id === selectedKanbanId.value) ||
    null
);
const selectedStageOption = computed(
  () =>
    stageOptions.value.find(option => option.id === selectedStageId.value) ||
    null
);

const onKanbanChange = option => {
  selectedKanbanId.value = option?.id || null;
  selectedStageId.value = null;
  emit('update:modelValue', null);
};

const onStageChange = option => {
  selectedStageId.value = option?.id || null;
  emit('update:modelValue', selectedStageId.value);
};

onMounted(() => {
  if (!kanbans.value.length) store.dispatch('kanbans/get');
});
</script>

<template>
  <div class="flex items-center gap-2">
    <SingleSelect
      :model-value="selectedKanbanOption"
      :options="kanbanOptions"
      :dropdown-max-height="dropdownMaxHeight"
      :placeholder="$t('KANBAN_SETTINGS.STAGE_CASCADE.SELECT_KANBAN')"
      class="flex-1"
      @update:model-value="onKanbanChange"
    />
    <SingleSelect
      :model-value="selectedStageOption"
      :options="stageOptions"
      :dropdown-max-height="dropdownMaxHeight"
      :placeholder="$t('KANBAN_SETTINGS.STAGE_CASCADE.SELECT_STAGE')"
      class="flex-1"
      @update:model-value="onStageChange"
    />
  </div>
</template>
